import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ENROLMENT_LIMITS, PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, passwordChangeSchema, passwordResetSchema } from '@vtf/shared';
import { api, errorText, setSession } from '../lib/api';
import { Button, Card, ErrorText, Field, Input } from '../components/ui';
import { PublicFrame } from './Apply';

/**
 * INT-ENR-05: "Forgot password?", the one-time reset link (from the email or from an Admin) and "Change password" in
 * Settings. The API's answers are authoritative; the shared zod schemas only give early feedback.
 */

const firstIssue = (r: { success: boolean; error?: { issues: { message: string }[] } }) => (r.success ? null : (r.error?.issues[0]?.message ?? 'Check the form.'));

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function send(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      setMsg((await api<{ message: string }>('/password/forgot', { body: { email, ...(website ? { website } : {}) } })).message);
    } catch (x) {
      setErr(errorText(x));
    } finally {
      setBusy(false);
    }
  }

  return (
    <PublicFrame>
      <Card className="flex flex-col gap-4 p-6">
        <h1 className="m-0 text-lg font-bold">Forgot your password?</h1>
        {msg ? (
          <p role="status" className="m-0 text-sm text-up">
            {msg}
          </p>
        ) : (
          <form onSubmit={send} className="flex flex-col gap-4">
            <p className="m-0 text-sm text-sub">Enter the email you sign in with. If it belongs to an active floor account, we’ll email a one-time link to choose a new password. You’ll still need your authenticator app.</p>
            <Field label="Email">
              <Input type="email" autoComplete="username" required maxLength={ENROLMENT_LIMITS.email} value={email} onChange={(e) => setEmail(e.target.value)} />
            </Field>
            {/* Honeypot: hidden from people and screen readers; automated requests fill it in. */}
            <div aria-hidden="true" className="absolute -left-[9999px] h-px w-px overflow-hidden">
              <input tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
            </div>
            <ErrorText error={err} />
            <Button type="submit" variant="primary" disabled={busy}>
              {busy ? 'Sending…' : 'Email me a reset link'}
            </Button>
          </form>
        )}
        <p className="m-0 text-xs text-muted">No email arriving? The floor admin can also make a reset link for you.</p>
        <Link to="/login" className="text-sm">
          Back to sign in
        </Link>
      </Card>
    </PublicFrame>
  );
}

interface ResetView {
  email: string;
  name: string;
  totpRequired: boolean;
  expiresAt: string;
}

export function ResetPassword() {
  const { token } = useParams();
  const nav = useNavigate();
  const view = useQuery({ queryKey: ['password-reset', token], queryFn: () => api<ResetView>(`/password/reset/${token}`), retry: false, staleTime: Infinity });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [code, setCode] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const body = { password, code: code.trim() || undefined };
    const issue = firstIssue(passwordResetSchema.safeParse(body));
    if (issue) return setError(issue);
    if (password !== confirm) return setError('The two passwords don’t match.');
    if (view.data?.totpRequired && !body.code) return setError('Enter the 6-digit code from your authenticator app.');
    setBusy(true);
    try {
      const r = await api<{ message: string }>(`/password/reset/${token}`, { body });
      // any session this browser had was just revoked on the server
      setSession(null, null);
      setDone(r.message);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  if (view.isLoading)
    return (
      <PublicFrame>
        <Card className="p-6 text-sub">Loading…</Card>
      </PublicFrame>
    );
  if (view.error && !done)
    return (
      <PublicFrame>
        <Card tone="warn" className="flex flex-col gap-3 p-6">
          <h1 className="m-0 text-lg font-bold">This reset link doesn’t work</h1>
          <p className="m-0 text-sm text-sub">{errorText(view.error)}</p>
          <div className="flex gap-4 text-sm">
            <Link to="/forgot-password">Get a new link</Link>
            <Link to="/login">Go to sign in</Link>
          </div>
        </Card>
      </PublicFrame>
    );

  return (
    <PublicFrame>
      <Card className="flex flex-col gap-4 p-6">
        <h1 className="m-0 text-lg font-bold">Choose a new password</h1>
        {done ? (
          <>
            <p role="status" className="m-0 text-sm text-up">
              {done}
            </p>
            <Button variant="primary" onClick={() => nav('/login')}>
              Go to sign in
            </Button>
          </>
        ) : (
          view.data && (
            <form onSubmit={submit} className="flex flex-col gap-4">
              <Field label="Email">
                <Input value={view.data.email} readOnly autoComplete="username" />
              </Field>
              <Field label="New password" hint={`At least ${PASSWORD_MIN_LENGTH} characters. A short sentence is easy to remember and hard to guess.`}>
                <Input type="password" autoComplete="new-password" minLength={PASSWORD_MIN_LENGTH} maxLength={PASSWORD_MAX_LENGTH} required value={password} onChange={(e) => setPassword(e.target.value)} />
              </Field>
              <Field label="Type it again">
                <Input type="password" autoComplete="new-password" maxLength={PASSWORD_MAX_LENGTH} required value={confirm} onChange={(e) => setConfirm(e.target.value)} />
              </Field>
              {view.data.totpRequired && (
                <Field label="Authenticator code" hint="The 6-digit code from your authenticator app. A reset never skips two-step sign-in.">
                  <Input inputMode="numeric" autoComplete="one-time-code" required value={code} onChange={(e) => setCode(e.target.value)} mono />
                </Field>
              )}
              <p className="m-0 text-xs text-muted">Saving signs you out on every device. The link works once.</p>
              <ErrorText error={error} />
              <Button type="submit" variant="primary" disabled={busy}>
                {busy ? 'Saving…' : 'Save new password'}
              </Button>
            </form>
          )
        )}
      </Card>
    </PublicFrame>
  );
}

/** Settings card: current password + new password (+ 2FA code). Keeps this sign-in, ends the others. */
export function ChangePassword({ totpEnabled }: { totpEnabled: boolean }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ currentPassword: '', newPassword: '', confirm: '', code: '' });
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setOk(null);
    const body = { currentPassword: f.currentPassword, newPassword: f.newPassword, code: f.code.trim() || undefined };
    const issue = firstIssue(passwordChangeSchema.safeParse(body));
    if (issue) return setError(issue);
    if (f.newPassword !== f.confirm) return setError('The two new passwords don’t match.');
    setBusy(true);
    try {
      const r = await api<{ accessToken: string; signedOutSessions: number }>('/auth/password', { body });
      setSession(r.accessToken);
      void qc.invalidateQueries({ queryKey: ['sessions'] });
      setF({ currentPassword: '', newPassword: '', confirm: '', code: '' });
      setOk(`Password changed.${r.signedOutSessions ? ` Signed out ${r.signedOutSessions} other sign-in${r.signedOutSessions === 1 ? '' : 's'}.` : ''} We emailed you a note about it.`);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="flex flex-col gap-3 p-4">
      <h2 className="m-0 text-[15px] font-semibold">Change password</h2>
      <form onSubmit={submit} className="flex flex-col gap-3">
        <Field label="Current password">
          <Input type="password" autoComplete="current-password" required value={f.currentPassword} onChange={(e) => setF({ ...f, currentPassword: e.target.value })} />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="New password" hint={`At least ${PASSWORD_MIN_LENGTH} characters`}>
            <Input type="password" autoComplete="new-password" minLength={PASSWORD_MIN_LENGTH} maxLength={PASSWORD_MAX_LENGTH} required value={f.newPassword} onChange={(e) => setF({ ...f, newPassword: e.target.value })} />
          </Field>
          <Field label="New password again">
            <Input type="password" autoComplete="new-password" maxLength={PASSWORD_MAX_LENGTH} required value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} />
          </Field>
        </div>
        {totpEnabled && (
          <Field label="Authenticator code" hint="The 6-digit code from your authenticator app">
            <Input inputMode="numeric" autoComplete="one-time-code" required value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} mono />
          </Field>
        )}
        <p className="m-0 text-xs text-muted">Your other devices are signed out; this one stays signed in.</p>
        <ErrorText error={error} />
        {ok && (
          <p role="status" className="m-0 text-sm text-up">
            {ok}
          </p>
        )}
        <Button type="submit" variant="primary" className="self-start" disabled={busy}>
          {busy ? 'Saving…' : 'Change password'}
        </Button>
      </form>
    </Card>
  );
}
